import { Store } from '@/store';
import logger from '@/utils/client-logger';
import { StateCreator } from 'zustand';

export interface IRouterInfo {
  refer_page_name: string;
  refer_url_path: string;
  refer_path_name: string;
}

// sessionStorage 管理工具函数
const STORAGE_KEYS = {
  CURRENT_ROUTE: 'zl-current-route-info',
  FROM_ROUTE: 'zl-from-route-info',
};

const getStorageItem = (key: string): IRouterInfo | null => {
  if (typeof window === 'undefined') return null;
  try {
    const item = sessionStorage.getItem(key);
    return item ? (JSON.parse(item) as IRouterInfo) : null;
  } catch (e) {
    logger.error(`getStorageItem ${key}`, e);
    return null;
  }
};

const setStorageItem = (key: string, value: IRouterInfo): void => {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.setItem(key, JSON.stringify(value));
  } catch (e) {
    logger.error(`setStorageItem ${key}`, e);
  }
};

export interface RouteActions {
  // 路径跟踪相关方法
  setCurrentRouteInfo: (
    { refer_page_name, refer_url_path, refer_path_name }: IRouterInfo,
    skipFromRoute?: boolean,
  ) => void;
  setFromRouterInfo: ({ refer_page_name, refer_url_path, refer_path_name }: IRouterInfo) => void;
  // 获取路由信息
  getCurrentRouteInfo: () => IRouterInfo | null;
  getFromRouteInfo: () => IRouterInfo | null;
  // 重置路由状态
  resetRouteState: () => void;
}

export type RouteSlice = RouteActions;

export const createRouteSlice: StateCreator<Store, [], [], RouteSlice> = (set) => {
  return {
    // 初始状态
    // Actions
    setCurrentRouteInfo: (
      { refer_page_name, refer_url_path, refer_path_name },
      skipFromRoute = false,
    ) => {
      // 获取当前路由信息
      const currentRouteInfo = getStorageItem(STORAGE_KEYS.CURRENT_ROUTE);

      // 如果有当前路由信息且不跳过设置来源路由，将其保存为来源路由信息
      if (currentRouteInfo && !skipFromRoute) {
        setStorageItem(STORAGE_KEYS.FROM_ROUTE, currentRouteInfo);
      }

      // 保存新的当前路由信息
      const newRouteInfo: IRouterInfo = {
        refer_page_name,
        refer_url_path,
        refer_path_name,
      };
      setStorageItem(STORAGE_KEYS.CURRENT_ROUTE, newRouteInfo);
    },

    setFromRouterInfo: ({ refer_page_name, refer_url_path, refer_path_name }) => {
      const fromRouteInfo: IRouterInfo = {
        refer_page_name,
        refer_url_path,
        refer_path_name,
      };
      setStorageItem(STORAGE_KEYS.FROM_ROUTE, fromRouteInfo);
    },

    getCurrentRouteInfo: () => {
      return getStorageItem(STORAGE_KEYS.CURRENT_ROUTE);
    },

    getFromRouteInfo: () => {
      return getStorageItem(STORAGE_KEYS.FROM_ROUTE);
    },

    resetRouteState: () => {
      if (typeof window !== 'undefined') {
        try {
          sessionStorage.removeItem(STORAGE_KEYS.CURRENT_ROUTE);
          sessionStorage.removeItem(STORAGE_KEYS.FROM_ROUTE);
        } catch (e) {
          logger.error('resetRouteState', e);
        }
      }
      set({});
    },
  };
};
